import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { ControlPogResetModRow } from '../../types';

type SortKey = 'tagName' | 'pogName' | 'modNumber' | 'modCount' | 'score' | 'coverage' | 'kind';

// String columns compare case-insensitively; numeric ones fall back to -1 so mods
// without a suggestion sink to the bottom in descending order.
const SORT_VALUES: Record<SortKey, (mod: ControlPogResetModRow) => string | number> = {
  tagName: (mod) => (mod.tagName ?? '').toLowerCase(),
  pogName: (mod) => (mod.suggested?.pogName ?? '').toLowerCase(),
  modNumber: (mod) => mod.suggested?.modNumber ?? -1,
  modCount: (mod) => mod.suggested?.modCount ?? -1,
  score: (mod) => mod.suggested?.score ?? -1,
  coverage: (mod) => mod.suggested?.coverage ?? -1,
  kind: (mod) => mod.suggested?.kind ?? '',
};

const SORTABLE_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'tagName', label: 'Tag' },
  { key: 'pogName', label: 'Suggested planogram' },
  { key: 'modNumber', label: 'POG mod #' },
  { key: 'modCount', label: 'POG mods' },
  { key: 'score', label: 'Score' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'kind', label: 'Kind' },
];

export const PogResetModsPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();

  const [mods, setMods] = useState<ControlPogResetModRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [hideUnsuggested, setHideUnsuggested] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'desc' | 'asc' } | null>(null);

  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    const load = async () => {
      toggleLoading(true);
      setLoaded(false);
      try {
        const response = await ApiService.fetchPogResetMods(token, storeName);
        if (!active) return;
        setMods(response.mods);
        setLoaded(true);
      } catch (err) {
        if (!active) return;
        showErrorToast(err instanceof Error ? err.message : 'Error loading mod suggestions');
      } finally {
        if (active) toggleLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeName]);

  const filteredMods = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = mods.filter((mod) => {
      if (hideUnsuggested && !mod.suggested) return false;
      if (!query) return true;
      return (
        (mod.tagName ?? '').toLowerCase().includes(query) ||
        (mod.suggested?.pogName ?? '').toLowerCase().includes(query) ||
        (mod.suggested?.pogNumber ?? '').toLowerCase().includes(query)
      );
    });
    if (!sort) {
      return matching;
    }

    const getValue = SORT_VALUES[sort.key];
    return [...matching].sort((a, b) => {
      const valueA = getValue(a);
      const valueB = getValue(b);
      const comparison =
        typeof valueA === 'string' && typeof valueB === 'string'
          ? valueA.localeCompare(valueB)
          : Number(valueA) - Number(valueB);
      return sort.dir === 'desc' ? -comparison : comparison;
    });
  }, [mods, search, hideUnsuggested, sort]);

  const cycleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev?.key !== key) {
        return { key, dir: 'desc' };
      }

      return prev.dir === 'desc' ? { key, dir: 'asc' } : null;
    });
  };

  const sortIndicator = (key: SortKey) =>
    sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '';

  const suggestedCount = mods.filter((mod) => mod.suggested).length;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to={`/stores/${storeName}/pog-reset`}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
            title="Back to reset tasks"
          >
            <ArrowBackIcon />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Mod tags — planogram suggestions</h1>
            <p className="text-sm text-gray-600">
              Every camera-covered mod and its best match from open reset tasks ({suggestedCount}/
              {mods.length} suggested)
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex items-center gap-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by tag or planogram…"
            className="w-64 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={hideUnsuggested}
              onChange={(e) => setHideUnsuggested(e.target.checked)}
            />
            Hide mods without a suggestion
          </label>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {SORTABLE_COLUMNS.map(({ key, label }) => (
                  <th key={key} className="px-4 py-2 text-left font-medium text-gray-500">
                    <button onClick={() => cycleSort(key)} className="hover:text-gray-900">
                      {label}
                      {sortIndicator(key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMods.map((mod) => (
                <tr
                  key={mod.modCoverageId}
                  onClick={() =>
                    mod.suggested &&
                    navigate(`/stores/${storeName}/pog-reset/${mod.suggested.taskId}`, {
                      state: {
                        taskIds: [
                          ...new Set(
                            filteredMods
                              .map((m) => m.suggested?.taskId)
                              .filter((id): id is number => id != null)
                          ),
                        ],
                      },
                    })
                  }
                  className={`hover:bg-gray-50 ${mod.suggested ? 'cursor-pointer' : ''}`}
                >
                  <td className="px-4 py-2 font-mono font-medium whitespace-nowrap">
                    {mod.tagName ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    {mod.suggested ? (
                      <>
                        <div className="font-medium">{mod.suggested.pogName ?? '—'}</div>
                        <div className="text-xs text-gray-500">{mod.suggested.pogNumber ?? ''}</div>
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">{mod.suggested?.modNumber ?? ''}</td>
                  <td className="px-4 py-2">{mod.suggested?.modCount ?? ''}</td>
                  <td className="px-4 py-2">
                    {mod.suggested?.score != null
                      ? `${Math.round(mod.suggested.score * 100)}%`
                      : ''}
                  </td>
                  <td className="px-4 py-2">
                    {mod.suggested?.coverage != null
                      ? `${Math.round(mod.suggested.coverage * 100)}%`
                      : ''}
                  </td>
                  <td className="px-4 py-2">
                    {mod.suggested && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          mod.suggested.kind === 'proposed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {mod.suggested.kind}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {loaded && filteredMods.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No mods match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
